import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { setUser, clearAuthError } from '../../data/actions/userAuthActions';
import { getUser, getAuthError, getUserLoading } from '../../data/selectors/userAuthSelectors';
import { useNavigate } from 'react-router-dom';
import { discoverOrg } from '../../services/sso';
import styles from './login.module.css';
import LoadingSpinner from '../loadingSpinner/LoadingSpinner';

const Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ssoDiscovery, setSsoDiscovery] = useState(null);
  const [ssoChecking, setSsoChecking] = useState(false);
  const debounceRef = useRef(null);
  const user = useSelector(getUser);
  const authError = useSelector(getAuthError);
  const userLoading = useSelector(getUserLoading);
  const dispatch = useDispatch();

  useEffect(() => {
    document.title = 'KNOW - Login';
  }, []);

  const handleEmailChange = ({ target }) => {
    setEmail(target.value);
    setSsoDiscovery(null);
    if (authError) dispatch(clearAuthError());
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const val = target.value;
    if (val && val.includes('@')) {
      debounceRef.current = setTimeout(() => handleEmailBlur(val), 400);
    }
  };

  const handleEmailBlur = async (emailVal) => {
    const val = (emailVal || email).toLowerCase().trim();
    if (!val || !val.includes('@')) return;
    setSsoChecking(true);
    try {
      const { data } = await discoverOrg(val);
      setSsoDiscovery(data);
    } catch {
      setSsoDiscovery(null);
    }
    setSsoChecking(false);
  };

  const handleSsoLogin = () => {
    if (ssoDiscovery?.ssoLoginUrl) {
      window.location.href = ssoDiscovery.ssoLoginUrl;
    }
  };

  const handlePasswordChange = ({ target }) => {
    setPassword(target.value);
    if (authError) dispatch(clearAuthError());
  };

  const handleLoginSubmit = (event) => {
    event.preventDefault();
    setLoading(true);
    dispatch(setUser(email, password)).then(() => {
      setLoading(false);
    });
  };

  const handlePasswordReset = () => {
    return navigate('/forgot-password', { replace: true });
  };

  const handleSignUp = () => {
    return navigate('/join-now', { replace: true });
  };

  useEffect(() => {
    if (user.email && !userLoading) {
      // Perform navigation
      if (window.location.hash) {
        navigate('/' + window.location.hash.slice(1));
      } else {
        navigate('/');
      }
    }
  }, [user.email, user._id, user.organizations, userLoading, navigate]);

  useEffect(() => {
    let timer;
    if (loading) {
      timer = setTimeout(() => {
        setLoading(false);
        alert('Unable to complete login.');
      }, 14000); // should be kept in sync with timeout in request.js
    }
    return () => {
      clearTimeout(timer);
    };
  }, [loading]);

  const getErrorMessage = () => {
    if (!authError) return null;
    if (authError.status === 409) {
      return authError.response?.message || authError.response;
    }
    return authError.response?.message || authError.response || 'Login failed. Please try again.';
  };

  if (loading) return <LoadingSpinner />;

  const isExclusiveSso = ssoDiscovery?.authMode === 'exclusive';

  return (
    <div className={styles.login} role="main" aria-label="login">
      <div className={styles.info} role="complementary">
        <p>
          <b>KNOW</b> is a dynamic clinical and educational tool that provides summaries of
          up-to-date- research in integrative oncology.
        </p>
        <p>
          <b>KNOW</b> is designed to help you quickly access pertinent information to make
          evidence-informed decisions.
        </p>
      </div>
      <form onSubmit={handleLoginSubmit}>
        <label>
          {' '}
          Email:
          <input
            type="text"
            onChange={handleEmailChange}
            onBlur={() => handleEmailBlur()}
            value={email}
            placeholder="email address"
            autoFocus
          />
        </label>
        {ssoChecking && <p className={styles.ssoChecking}>Checking for SSO...</p>}
        {ssoDiscovery && (
          <button
            type="button"
            className={`${styles.imAButton} ${styles.ssoButton}`}
            onClick={handleSsoLogin}
          >
            Continue with SSO
          </button>
        )}
        {!isExclusiveSso && (
          <>
            <label>
              {' '}
              Password:
              <div className={styles.passwordInputWrapper}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  onChange={handlePasswordChange}
                  value={password}
                  placeholder="password"
                />
                <button
                  type="button"
                  tabIndex={-1}
                  className={styles.togglePassword}
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <FaEye /> : <FaEyeSlash />}
                </button>
              </div>
            </label>
            <input type="submit" className={styles.imAButton} value="Login" />
          </>
        )}
      </form>
      {!isExclusiveSso && (
        <>
          <button className={styles.imAButton} onClick={handlePasswordReset}>
            Forgot your password?
          </button>
          <button className={styles.imAButton} onClick={handleSignUp}>
            Sign Up
          </button>
        </>
      )}
      {authError && <p className={styles.errorMessage}>{getErrorMessage()}</p>}
    </div>
  );
};

export default Login;
